import React, {memo, useCallback, useEffect, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {BridgeDeviceModel} from '../../../../types/bridge-device';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useKeyboard} from '../../../../hooks/keyboard';
import {useMessage} from '../../../../hooks/message';
import {useQuestion} from '../../../../hooks/question';
import {useRPC} from '../../../../hooks/rpc';
import {useSetSelector} from '../../../../hooks/set-selector';
import {useTranslation} from "react-i18next";

const BACK_URL = '/settings/device';

export const DeviceSettingsScreen = memo(function DeviceSettingsScreen() {
    const {t} = useTranslation();
    const params = useParams<{ id: string }>();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard, currentNumpadExtendedInput] =
        useKeyboard('numpad-extended', true);
    const {success, failure} = useMessage();
    const question = useQuestion();
    const [rpc] = useRPC();
    const history = useHistory();

    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);
    const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList);

    const [selected, setSelected] = useState<BridgeDeviceModel>(null);
    const [isTemperatureDevice, setTemperatureDevice] = useState(false);
    const [temperatureOffset, setTemperatureOffset] = useState('0');
    const [temperatureHysteresis, setTemperatureHysteresis] = useState('0.2');
    const [firstLevelAlarmCountdownDuration, setFirstLevelAlarmCountdownDuration] = useState('0');
    const [secondLevelAlarmCountdownDuration, setSecondLevelAlarmCountdownDuration] = useState('0');
    const [thirdLevelAlarmCountdownDuration, setThirdLevelAlarmCountdownDuration] = useState('0');
    const [reactivationDurationAfterReset, setReactivationDurationAfterReset] = useState('0');
    const [sirenIntervalOnDuration, setSirenIntervalOnDuration] = useState('0');
    const [sirenIntervalOffDuration, setSirenIntervalOffDuration] = useState('0');
    const [sirenRepeatCount, setSirenRepeatCount] = useState('0');
    const [matchInProgress, setMatchInProgress] = useState(false)

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const onSavePressed = useCallback(() => {
        const temperatureOffsetValue = Number(temperatureOffset);
        if (isTemperatureDevice) {
            if (isNaN(temperatureOffsetValue) ||
                temperatureOffsetValue < -9.9 ||
                temperatureOffsetValue > 9.9
            ) {
                failure(t('temperature_offset_value_must_be_between_-9.9_9.9'));

                return;
            }
        }

        const temperatureHysteresisValue = Number(temperatureHysteresis);
        if (isTemperatureDevice) {
            if (isNaN(temperatureHysteresisValue) ||
                temperatureHysteresisValue < 0 ||
                temperatureHysteresisValue > 25.5
            ) {
                failure(t('hysteresis_value_must_be_between_25.5_0'));

                return;
            }
        }

        const firstLevelAlarmCountdownDurationValue = Number(firstLevelAlarmCountdownDuration);
        if (isNaN(firstLevelAlarmCountdownDurationValue) || firstLevelAlarmCountdownDurationValue < 0) {
            failure(t('alarm_level_1_countdown_duration_is_invalid'));

            return;
        }

        const secondLevelAlarmCountdownDurationValue = Number(secondLevelAlarmCountdownDuration);
        if (isNaN(secondLevelAlarmCountdownDurationValue) || secondLevelAlarmCountdownDurationValue < 0) {
            failure(t('alarm_level_2_countdown_duration_is_invalid'));

            return;
        }

        const thirdLevelAlarmCountdownDurationValue = Number(thirdLevelAlarmCountdownDuration);
        if (isNaN(thirdLevelAlarmCountdownDurationValue) || thirdLevelAlarmCountdownDurationValue < 0) {
            failure(t('alarm_level_3_countdown_duration_is_invalid'));

            return;
        }

        const reactivationDurationAfterResetValue = Number(reactivationDurationAfterReset);
        if (isNaN(reactivationDurationAfterResetValue) || reactivationDurationAfterResetValue < 0) {
            failure(t('reactivation_after_alarm_reset_duration_is_invalid'));

            return;
        }

        const sirenIntervalOnDurationValue = Number(sirenIntervalOnDuration);
        if (isNaN(sirenIntervalOnDurationValue) || sirenIntervalOnDurationValue < 0) {
            failure(t('siren_interval_on_duration_is_invalid'));

            return;
        }

        const sirenIntervalOffDurationValue = Number(sirenIntervalOffDuration);
        if (isNaN(sirenIntervalOffDurationValue) || sirenIntervalOffDurationValue < 0) {
            failure(t('siren_interval_off_duration_is_invalid'));

            return;
        }

        const sirenRepeatCountValue = Number(sirenRepeatCount);
        if (isNaN(sirenRepeatCountValue) || sirenRepeatCountValue < 0) {
            failure(t('siren_repeat_count_is_invalid'));

            return;
        }

        const bridgeDeviceModel: BridgeDeviceModel = {
            ...selected,
            ...(isTemperatureDevice ? {temperature_offset: Math.round(temperatureOffsetValue * 10)} : {}),
            ...(isTemperatureDevice ? {temperature_hysteresis: Math.round(temperatureHysteresisValue * 10)} : {}),
            alarm_config: {
                first_level_alarm_countdown_duration: firstLevelAlarmCountdownDurationValue,
                second_level_alarm_countdown_duration: secondLevelAlarmCountdownDurationValue,
                third_level_alarm_countdown_duration: thirdLevelAlarmCountdownDurationValue,
                reactivation_duration_after_reset: reactivationDurationAfterResetValue,
                siren_interval_on_duration: sirenIntervalOnDurationValue,
                siren_interval_off_duration: sirenIntervalOffDurationValue,
                siren_interval_repeat_count: sirenRepeatCountValue,
            },
        };

        rpc.updateBridgeDevice(bridgeDeviceModel)
            .then(() => {
                success(t('successfully_saved'));
                onBackPressed();
            }).catch(failure);
    }, [
        t,
        isTemperatureDevice,
        temperatureOffset,
        temperatureHysteresis,
        firstLevelAlarmCountdownDuration,
        secondLevelAlarmCountdownDuration,
        thirdLevelAlarmCountdownDuration,
        reactivationDurationAfterReset,
        sirenIntervalOnDuration,
        sirenIntervalOffDuration,
        sirenRepeatCount,
        selected,
        success,
        failure,
        rpc,
        onBackPressed,
    ]);

    const onResetPressed = useCallback(() => {
        question(t('will_be_reset_continue'), () => {
            rpc.resetDevice(selected.gateway_id, selected.unique_device_id)
                .then(() => {
                    success(t('device_successfully_reset'));
                    setSelected({...selected, unique_device_id: ''});
                })
                .catch(failure);
        });
    }, [t, selected, question, rpc, success, failure]);


    const matchAgain = useCallback(() => {
        if (matchInProgress) {
            return
        }
        if (selected.unique_device_id === '') {
            return failure("Unique id bulunamadı.")
        }
        setMatchInProgress(true)
        rpc.reMatchDevice(selected.gateway_id, selected.unique_device_id, selected.id)
            .catch(failure)
            .finally(() => setMatchInProgress(false))
    }, [matchInProgress, selected, failure, rpc])

    const buttons = [
        {
            text: matchInProgress ? t('matching') : (t('again') + ' ' + t('match')),
            className: 'blue',
            onClick: matchAgain,
        },
        {
            text: t('reset'),
            className: 'orange',
            onClick: onResetPressed,
            hidden: !selected?.unique_device_id,
        },
        {
            text: t('save'),
            className: 'green',
            onClick: onSavePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        },
    ];

    const onBlur = useCallback(() => {
        setActiveNumpadInput(null);
        setActiveNumpadExtendedInput(null);
    }, [setActiveNumpadInput, setActiveNumpadExtendedInput]);

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const device = bridgeDeviceList.find(device => device.id === params['id']);
        if (device == null) {
            return;
        }

        setSelected(device);
        setTemperatureDevice(bridgeDeviceTypeSet[device.type_id]?.is_temperature_device || false);
        setTemperatureOffset((device.temperature_offset / 10).toFixed(1));
        setTemperatureHysteresis((device.temperature_hysteresis / 10).toFixed(1));
        setFirstLevelAlarmCountdownDuration(device.alarm_config.first_level_alarm_countdown_duration.toString());
        setSecondLevelAlarmCountdownDuration(device.alarm_config.second_level_alarm_countdown_duration.toString());
        setThirdLevelAlarmCountdownDuration(device.alarm_config.third_level_alarm_countdown_duration.toString());
        setReactivationDurationAfterReset(device.alarm_config.reactivation_duration_after_reset.toString());
        setSirenIntervalOnDuration(device.alarm_config.siren_interval_on_duration.toString());
        setSirenIntervalOffDuration(device.alarm_config.siren_interval_off_duration.toString());
        setSirenRepeatCount(device.alarm_config.siren_interval_repeat_count.toString());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                className='service-edit'
                buttons={buttons}
                backUrl={BACK_URL}
                currentNumpadInput={currentNumpadInput}
                currentNumpadExtendedInput={currentNumpadExtendedInput}>
                {
                    renderIf(selected)(() =>
                        <div className='content-divider'>
                            <div className='content-wrapper'>
                                <>
                                    <div className='line'>
                                        <span>{t('alarm_countdown_durations')}</span>
                                    </div>
                                    <SettingInputComponent
                                        placeholder={t('1st_level')}
                                        label={t('1st_level')}
                                        value={firstLevelAlarmCountdownDuration}
                                        setValue={setFirstLevelAlarmCountdownDuration}
                                        setActiveInput={setActiveNumpadInput}
                                        tempValue='0'
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('2nd_level')}
                                        label={t('2nd_level')}
                                        value={secondLevelAlarmCountdownDuration}
                                        setValue={setSecondLevelAlarmCountdownDuration}
                                        setActiveInput={setActiveNumpadInput}
                                        tempValue='0'
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('3rd_level')}
                                        label={t('3rd_level')}
                                        value={thirdLevelAlarmCountdownDuration}
                                        setValue={setThirdLevelAlarmCountdownDuration}
                                        setActiveInput={setActiveNumpadInput}
                                        tempValue='0'
                                        onBlur={onBlur}/>
                                    <div className='line'>
                                        <span>{t('alar_reactivation_duration_after_reset')}</span>
                                    </div>
                                    <SettingInputComponent
                                        placeholder={t('reactivation_duration')}
                                        label={t('reactivation_duration_2')}
                                        value={reactivationDurationAfterReset}
                                        setValue={setReactivationDurationAfterReset}
                                        setActiveInput={setActiveNumpadInput}
                                        tempValue='0'
                                        onBlur={onBlur}/>
                                </>
                            </div>
                            <div className='content-wrapper'>
                                <>
                                    <div className='line'>
                                        <span>{t('siren_interval')}</span>
                                    </div>
                                    <SettingInputComponent
                                        placeholder={t('interval_on_duration')}
                                        label={t('interval_on_duration_2')}
                                        value={sirenIntervalOnDuration}
                                        setValue={setSirenIntervalOnDuration}
                                        setActiveInput={setActiveNumpadInput}
                                        tempValue='0'
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('interval_off_duration')}
                                        label={t('interval_off_duration_2')}
                                        value={sirenIntervalOffDuration}
                                        setValue={setSirenIntervalOffDuration}
                                        setActiveInput={setActiveNumpadInput}
                                        tempValue='0'
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('interval_repeat_count')}
                                        label={t('interval_repeat_count_2')}
                                        value={sirenRepeatCount}
                                        setValue={setSirenRepeatCount}
                                        setActiveInput={setActiveNumpadInput}
                                        tempValue='0'
                                        onBlur={onBlur}/>
                                    <div className='line'>
                                        <span>{t('temperature')}</span>
                                    </div>
                                    <SettingInputComponent
                                        placeholder={t('temperature_offset_value')}
                                        label={t('temperature_offset')}
                                        value={temperatureOffset}
                                        setValue={setTemperatureOffset}
                                        setActiveInput={setActiveNumpadExtendedInput}
                                        tempValue='0'
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('temperature_hysteresis_value')}
                                        label={t('temperature_hysteresis')}
                                        value={temperatureHysteresis}
                                        setValue={setTemperatureHysteresis}
                                        setActiveInput={setActiveNumpadExtendedInput}
                                        tempValue='0'
                                        onBlur={onBlur}/>
                                </>
                            </div>
                        </div>,
                    )
                }
            </SettingsPageContainer>
            {
                renderNumpadKeyboard()
            }
            {
                renderNumpadExtendedKeyboard()
            }
        </PasswordContainer>
    );
});
